import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Plus,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  MoreHorizontal,
  HelpCircle,
  FileText,
  ClipboardList
} from 'lucide-react';

import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import ConfirmationModal from '../common/ConfirmationModal';

import SourcingRfpService from '../../services/sourcingRFPService';

//////////////////////////////////////////////////////////////
// ACTION DROPDOWN COMPONENT (Updated Version)
//////////////////////////////////////////////////////////////

const ActionDropdown = ({ rfp, onView, onEdit, onPublish, onDelete, onStartEvaluation }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  // Close on outside click or scroll
  useEffect(() => {
    const close = (e) => {
      if (
        !menuRef.current?.contains(e.target) &&
        !buttonRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", close);
      window.addEventListener("scroll", close, true);
    }

    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  const rect = buttonRef.current?.getBoundingClientRect();

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 transition-all duration-200 shadow-sm ${open ? 'bg-gray-100 border-gray-400' : 'bg-white hover:bg-gray-50'}`}
      >
        <MoreHorizontal size={16} className={open ? 'text-blue-600' : 'text-gray-600'} />
      </button>

      {open && rect && createPortal(
        <div
          ref={menuRef}
          style={{ position: "fixed", top: rect.bottom + 6, left: rect.left - 144, zIndex: 9999 }}
          className="w-48 bg-white border border-gray-200 rounded-lg shadow-xl py-1"
        >
          {/* VIEW: Available for all statuses */}
          <MenuItem 
            icon={<Eye size={16} />} 
            label="View Details" 
            onClick={() => { setOpen(false); onView(rfp); }} 
          />

          {/* START EVALUATION: Only visible when Published */}
          {rfp?.status === "PUBLISHED" && (
            <MenuItem
              icon={<ClipboardList size={16} />}
              label="Start Evaluation"
              onClick={() => {
                setOpen(false);
                onStartEvaluation(rfp);
              }}
            />
          )}

          {/* DRAFT ACTIONS: Edit, Publish, Delete */}
          {rfp?.status === "DRAFT" && (
            <>
              <MenuItem 
                icon={<Edit size={16} />} 
                label="Edit RFP" 
                onClick={() => { setOpen(false); onEdit(rfp); }} 
              />
              <MenuItem 
                icon={<FileText size={16} />} 
                label="Publish RFP" 
                onClick={() => { setOpen(false); onPublish(rfp); }} 
              />
              <div className="border-t border-gray-100 my-1" />
              <MenuItem 
                icon={<Trash2 size={16} />} 
                label="Delete" 
                danger 
                onClick={() => { setOpen(false); onDelete(rfp); }} 
              />
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

///////////////////////////////////////////////////////////
// MENU ITEM HELPER
///////////////////////////////////////////////////////////

const MenuItem = ({ icon, label, onClick, danger }) => (
  <button
    onClick={(e) => {
      e.stopPropagation(); // Prevent row clicks if any
      onClick();
    }}
    className={`
      flex items-center gap-2
      w-full px-3 py-2 text-sm
      transition-colors duration-150
      ${danger
        ? "text-red-600 hover:bg-red-50"
        : "text-gray-700 hover:bg-gray-100"}
    `}
  >
    <span className={danger ? "text-red-500" : "text-gray-400"}>
      {icon}
    </span>
    {label}
  </button>
);

//////////////////////////////////////////////////////////////
// MAIN COMPONENT
//////////////////////////////////////////////////////////////

const RfpManagement = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [rfps, setRfps] = useState([]);
  const [loading, setLoading] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [rfpToDelete, setRfpToDelete] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    itemsPerPage: 10,
    totalItems: 0,
    pages: 1,
  });


// LOAD RFPs

  const loadRfps = async () => {
    setLoading(true);

    try {
      const response = await SourcingRfpService.getRfps({
        page: pagination.page,
        limit: pagination.itemsPerPage,
      });

      setRfps(response.data || []);

      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          totalItems: response.pagination.total,
          pages: response.pagination.pages,
        }));
      }

    } catch (err) {
      showError(err.message || 'Failed to load RFPs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRfps();
  }, [pagination.page]);

// ACTION HANDLERS


  const handleView = (rfp) => {
    navigate(`/sourcing/rfps/view/${rfp._id}`);
  };

  const handleEdit = (rfp) => {
    navigate(`/sourcing/rfps/edit/${rfp._id}`);
  };

  const handleDelete = (rfp) => {
    setRfpToDelete(rfp);
    setDeleteModal(true);
  };
  
  const handlePublish = async (rfp) => {
    // Optional: Add a confirmation check before publishing
    if (!window.confirm(`Are you sure you want to publish "${rfp.title}"? Once published, vendors can see it.`)) {
      return;
    }

    setLoading(true);
    try {
      // 1. Call the service
      await SourcingRfpService.publishRfp(rfp._id);

      // 2. Show success message
      success(`RFP "${rfp.title}" has been published successfully!`);

      // 3. Refresh the list to show updated status
      loadRfps();
    } catch (err) {
      showError(err.message || 'Failed to publish RFP');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEvaluation = async (rfp) => {
  // 1. Confirm with user
  if (!window.confirm("Move this RFP to Evaluation? This signifies bidding is closed and scoring has begun.")) {
    return;
  }

  setLoading(true);
  try {
    // FIX: Change 'updateStatus' to 'startEvaluation' to match your Service file
    await SourcingRfpService.startEvaluation(rfp._id);
    
    success(`RFP "${rfp.title}" is now in Evaluation.`);
    loadRfps(); // Refresh the table
  } catch (err) {
    // Use the error helper to get the message string
    const errorMsg = err.message || "Could not start evaluation";
    showError(errorMsg);
  } finally {
    setLoading(false);
  }
};

  // Inside RfpManagement component
const confirmDelete = async () => {
  try {
    // 1. Call the service (ensure this matches the name in your SourcingRfpService)
    await SourcingRfpService.deleteRfp(rfpToDelete._id);

    // 2. Show success notification
    success(`RFP "${rfpToDelete.title}" deleted successfully`);
    
    // 3. Close the modal
    setDeleteModal(false);
    setRfpToDelete(null);

    // 4. Refresh the list
    loadRfps();
  } catch (err) {
    // Use your showError helper
    showError(err.message || 'Failed to delete RFP');
  }
};

   const getStatusPill = (status) => {
  const config = {
    DRAFT: {
      bg: '#f3f4f6',
      color: '#6b7280',
      dot: '#6b7280',
      label: 'Draft',
    },
    PUBLISHED: {
      bg: '#dbeafe',
      color: '#2563eb',
      dot: '#2563eb',
      label: 'Published',
    },
    EVALUATION: {
      bg: '#fef3c7',
      color: '#d97706',
      dot: '#d97706',
      label: 'Evaluation',
    },
    AWARDED: {
      bg: '#d1fae5',
      color: '#059669',
      dot: '#059669',
      label: 'Awarded',
    },
    CANCELLED: {
      bg: '#fee2e2',
      color: '#dc2626',
      dot: '#dc2626',
      label: 'Cancelled',
    },
  };

  const s = config[status] || config.DRAFT;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '999px',
        fontSize: '12px',
        fontWeight: 600,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.color}20`,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: s.dot,
        }}
      />
      {s.label}
    </span>
  );
};

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
    key: 'rfpNumber',
    label: 'RFP ID',
    render: (_, rfp) => (
      <span style={{ fontWeight: 600 }}>
        {rfp.rfpNumber}
      </span>
    ),
  },
  {
    key: 'title',
    label: 'RFP Title',
    render: (_, rfp) => (
      <div>
        <div style={{ fontWeight: 600 }}>
          {rfp.title}
        </div>
      </div>
    ),
  },
  {
    key: 'responseDeadline',
    label: 'Deadline',
    render: (_, rfp) =>
      rfp.responseDeadline
        ? new Date(rfp.responseDeadline).toLocaleDateString()
        : '-',
  },
  {
    key: 'status',
    label: 'Status',
    render: (_, rfp) => getStatusPill(rfp.status),
  },
  {
    key: 'actions',
    label: '',
    width: '60px',
    render: (_, rfp) => (
      <ActionDropdown
        rfp={rfp}
        onView={handleView}
        onEdit={handleEdit}
        onPublish={handlePublish}
        onDelete={rfp.status === 'DRAFT' ? handleDelete : null}
        onStartEvaluation={handleStartEvaluation}
      />
    ),
  },
];

  const headerActions = [
    {
      icon: <HelpCircle size={18} />,
      variant: 'outline',
      onClick: () => window.open('/help/rfp-management'),
    },
    {
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      variant: 'outline',
      onClick: loadRfps,
    },
    {
      label: 'Create RFP',
      icon: <Plus size={18} />,
      variant: 'primary',
      onClick: () => navigate('/sourcing/rfps/create'),
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="RFP Management"
        description="Create, publish and evaluate sourcing events"
        actions={headerActions}
      />

      <div className="page-container">
        <DataTable
          data={rfps}
          columns={columns}
          loading={loading}
          showActions={false}
          pagination={{
            currentPage: pagination.page,
            totalPages: pagination.pages,
            total: pagination.totalItems,
            itemsPerPage: pagination.itemsPerPage,
          }}
          onPageChange={(page) =>
            setPagination(prev => ({ ...prev, page }))
          }
          emptyMessage="No RFPs found. Click 'Create RFP' to begin."
        />
      </div>

      <ConfirmationModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete RFP"
        message={`Are you sure you want to delete "${rfpToDelete?.title}"?`}
        confirmText="Delete"
        type="danger"
      />

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default RfpManagement;